import { service } from "@ember/service";
import Controller from "@ember/controller";
import { action } from "@ember/object";
import { task, dropTask } from "ember-concurrency";
import { guidFor } from "@ember/object/internals";
import { next } from "@ember/runloop";
import ENV from "glesys-controlpanel/config/environment";

export default class DnsDomainController extends Controller {
  @service ajax;
  @service current;
  @service modal;
  @service intl;
  @service notification;
  @service router;
  @service abilities;
  @service store;

  get project() {
    return this.model.project;
  }

  get organization() {
    return this.model.organization;
  }

  get projectId() {
    return this.project.id;
  }

  get domain() {
    return this.model.domain;
  }

  get prices() {
    return this.model.prices.pricelist;
  }

  get expireDate() {
    return this.domain.registrarinfo.expire;
  }

  get state() {
    return this.domain.state;
  }

  get canAccessDnssec() {
    return this.abilities.can("enable dnssec for organization", this.organization);
  }

  get tldSupportsDnssec() {
    return this.domain.dnssec?.tldsupport === "yes";
  }

  get dnssecRecords() {
    return this.domain.dnssecRecords || [];
  }

  get hasDnssecRecords() {
    return this.dnssecRecords.length > 0;
  }

  get supportsCustomDnssecRecords() {
    return this.tldSupportsDnssec && this.domain.isGlesysRegistrarOnly;
  }

  get hasCustomDnssecRecords() {
    return this.domain.isGlesysRegistrarOnly && this.hasDnssecRecords;
  }

  get dnssecStatusText() {
    if (!this.tldSupportsDnssec) {
      return this.intl.t("dns.domain.dnssec.labels.unavailable");
    }

    if (this.domain.dnssec.enabled === "yes") {
      return this.intl.t("label.on");
    }

    if (this.hasCustomDnssecRecords) {
      return this.intl.t("dns.domain.dnssec.labels.custom");
    }

    if (this.supportsCustomDnssecRecords) {
      return this.intl.t("dns.domain.dnssec.labels.not-configured");
    }

    return this.intl.t("label.off");
  }

  get tabs() {
    return [
      { name: this.intl.t("link.domain.dns-records"), route: "dns.domain.dns-records" },
      this.canAccessDnssec ? { name: this.intl.t("link.domain.dnssec"), route: "dns.domain.dnssec" } : null,
    ].filter((item) => item !== null);
  }

  get canRenew() {
    let { state } = this;
    if (state === "glesys-ok" || state === "glesys-expiring" || this.domain.isRenewFail) {
      return true;
    }
    return false;
  }

  get domainPrice() {
    let prices = this.prices;
    let domainName = this.domain.domainname;
    let tld = domainName.split(".").pop(-1);
    let filteredPrices = prices[tld];
    return filteredPrices;
  }

  renewDomain = task(async (numyears) => {
    let url = `${ENV.apiBaseUri}/domain/renew`;
    await this.ajax.post(url, {
      data: {
        domainname: this.domain.id,
        numyears,
      },
    });

    this.send("reloadDetails");
  });

  changeNameServers = task(async (nameservers) => {
    let url = `${ENV.apiBaseUri}/domain/changenameservers`;

    let filteredNameServers = Object.fromEntries(Object.entries(nameservers).filter(([, value]) => value));

    await this.ajax.post(url, {
      data: {
        domainname: this.domain.id,
        ...filteredNameServers,
      },
    });

    this.send("reloadDetails");
  });

  toggleAutorenew = dropTask(async () => {
    this.domain.set("registrarinfo.autorenew", this.domain.registrarinfo.autorenew === "yes" ? "no" : "yes");
    await this.ajax.post(`${ENV.apiBaseUri}/domain/setautorenew`, {
      data: {
        domainname: this.domain.id,
        autorenew: this.domain.registrarinfo.autorenew,
      },
    });
  });

  @action
  addRecord(type) {
    if (this.router.currentRouteName !== "dns.domain.dns-records") {
      this.router.transitionTo("dns.domain.dns-records").then(() => {
        this.addRecord(type);
      });
      return;
    }

    let domain = this.domain;
    let record = this.store.createRecord("dns-record", { type, domain, rdata: type === "MX" ? "10" : "" });
    let id = guidFor(record);

    next(() => {
      document.querySelector(`#dns-record-${id} ._host-input`)?.focus();
    });
  }
}
